import { useTranslation } from 'react-i18next'
import c from './assets/sass/list.module.scss'
import cn from 'classnames'

import { useEffect, useState, useCallback } from 'react'
import { userService } from '../../../core/services'
import { useWindowActions } from '../../../models/window'
import { modalNames } from '../../modal/constants/names'
import { useAutoTranslateList } from '../../../core/hooks/useAutoTranslate'

const GAME_LABELS = { STANDOFF_2: 'SO2', PUBG_MOBILE: 'PUBGM' }
const getGameLabel = (game) => GAME_LABELS[game] || ''

const EyeOffIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFD159" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ cursor: 'pointer', marginLeft: '6px', verticalAlign: 'middle', flexShrink: 0 }}>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
        <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
)

const PromoSpoiler = ({ code }) => {
    const [revealed, setRevealed] = useState(false)
    if (!code) return null
    const label = code.length > 10 ? 'ОТОБРАЗИТЬ ПРОМОКОД' : 'ОТОБРАЗИТЬ КОД'
    return (
        <span
            onClick={(e) => { e.stopPropagation(); if (!revealed) setRevealed(true) }}
            style={{
                marginTop: '4px',
                fontSize: '13px',
                fontWeight: '600',
                letterSpacing: revealed ? '0.5px' : '0',
                color: revealed ? '#FFD159' : 'transparent',
                background: revealed ? 'none' : 'rgba(255, 209, 89, 0.15)',
                borderRadius: '4px',
                padding: revealed ? '0' : '2px 8px',
                cursor: revealed ? 'default' : 'pointer',
                userSelect: revealed ? 'text' : 'none',
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                transition: 'all 0.3s ease',
                overflowWrap: 'break-word',
                wordBreak: 'break-all',
            }}
        >
            {revealed ? (
                <>
                    <span>{code}</span>
                    <span onClick={(e) => { e.stopPropagation(); setRevealed(false) }}>
                        <EyeOffIcon />
                    </span>
                </>
            ) : (
                <span style={{ color: '#FFD159', fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px' }}>
                    {label}
                </span>
            )}
        </span>
    )
}

const formatDate = (dateStr) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    return `${day}.${month}.${year} ${hours}:${minutes}`
}

export const List = () => {
    const { t } = useTranslation()
    const [rawItems, setRawItems] = useState([])
    const { openWindow } = useWindowActions()
    
    // Автоперевод названий и описаний наград
    const items = useAutoTranslateList(rawItems, ['title', 'description', 'amount'])

    const loadHistory = async () => {
        try {
            const res = await userService.getWithdrawHistory()
            setRawItems(res?.items || [])
        } catch {}
    }

    useEffect(() => {
        loadHistory()
    }, [])

    const handleClaimCase = (item) => {
        openWindow(modalNames.CASE_MODAL, {
            caseData: {
                id: item.id,
                caseId: item.caseId,
                name: item.title,
                price: item.price,
                imageUrl: item.imageUrl,
                promoCode: item.promoCode,
                isHistory: true,
            },
            onClaimed: () => {
                setRawItems((prevItems) =>
                    prevItems.map((it) =>
                        it.id === item.id && (it.type === 'CASE' || it.type === 'REFERRAL_CASE')
                            ? { ...it, status: 'VIEWED' }
                            : it
                    )
                )
            },
        })
    }

    if (items.length === 0) {
        return (
            <div className={c.empty}>
                <p>{t('history.empty')}</p>
            </div>
        )
    }

    return (
        <ul className={c.component}>
            {items.map((it) => (
                <li
                    key={`${it.type}_${it.id}`}
                    className={cn(c.item, (it.type === 'CASE' || it.type === 'REFERRAL_CASE') ? c.case : c.coin)}
                >
                    <div className={c.left}>
                        {(it.type === 'CASE' || it.type === 'REFERRAL_CASE') ? (
                            <>
                                <img
                                    src={it.imageUrl || '/images/case-01.png'}
                                    alt="Case"
                                />
                                <div className={c.leftText}>
                                    <span className={c.type}>
                                        {it.title}
                                        {it.game && getGameLabel(it.game) ? ` (${getGameLabel(it.game)})` : ''}
                                    </span>
                                    <span className={c.description}>
                                        {it.type === 'REFERRAL_CASE'
                                            ? t('history.referralCase', { count: it.threshold })
                                            : t('history.case')}
                                    </span>
                                    <PromoSpoiler code={it.promoCode} />
                                </div>
                            </>
                        ) : it.type === 'REFERRAL_COINS' ? (
                            <div className={c.leftText}>
                                <span className={c.type}>{it.amount}</span>
                                <span className={c.description}>{it.title}</span>
                            </div>
                        ) : it.type === 'LEADERBOARD' ? (
                            <div className={c.leftText}>
                                <span className={c.type}>{it.amount}</span>
                                <span className={c.description}>{it.title}</span>
                            </div>
                        ) : it.type === 'ADVENT' ? (
                            <div className={c.leftText}>
                                <span className={c.type}>
                                    {it.amount || t('history.coins')}
                                </span>
                                <span className={c.description}>
                                    {it.title ||
                                        it.description ||
                                        t('history.day', { day: it.day })}
                                </span>
                                <PromoSpoiler code={it.promoCode} />
                            </div>
                        ) : (
                            <div className={c.leftText}>
                                <span className={c.type}>
                                    {t('history.reward')}
                                </span>
                                <span className={c.description}>
                                    {it.description || t('history.received')}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className={c.right}>
                        <div className={c.rightText}>
                            {(it.type === 'CASE' || it.type === 'REFERRAL_CASE') && it.status === 'NEW' ? (
                                <>
                                    <span className={c.date}>
                                        {formatDate(it.purchasedAt || it.date)}
                                    </span>
                                    <button
                                        className={c.get}
                                        onClick={() => handleClaimCase(it)}
                                    >
                                        {t('history.get')}
                                    </button>
                                </>
                            ) : (it.type === 'CASE' || it.type === 'REFERRAL_CASE') && it.status === 'VIEWED' ? (
                                <>
                                    <span className={c.date}>
                                        {formatDate(it.date)}
                                    </span>
                                    <span className={c.status}>
                                        {t('history.received')}
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span className={c.date}>
                                        {formatDate(it.date)}
                                    </span>
                                    <span className={c.status}>
                                        {t('history.received')}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                </li>
            ))}
        </ul>
    )
}
